<template>
  <div>
    <div class="page-header">
      <h1>📖 帳本查詢</h1>
      <p>查看和管理你的記帳記錄</p>
    </div>

    <!-- 時間區間按鈕 -->
    <div class="filter-bar">
      <button v-for="btn in filterButtons" :key="btn.key"
        :class="['filter-btn', { active: activeFilter === btn.key }]"
        @click="btn.action()">
        {{ btn.icon }} {{ btn.label }}
      </button>
    </div>

    <!-- 搜尋 & 進階篩選 -->
    <div v-if="!loading && tableData.length > 0" class="search-panel">
      <!-- 搜尋框 -->
      <div class="search-row">
        <div class="search-input-wrap">
          <span class="search-icon">🔍</span>
          <input
            v-model="search"
            class="search-input"
            placeholder="模糊搜尋（關鍵字跨欄比對）…"
            @input="onSearchInput"
          />
          <button v-if="search" class="search-clear" @click="search = ''">✕</button>
        </div>
        <!-- 進階篩選展開按鈕（只有流水帳顯示） -->
        <button v-if="tablefomrat === 1" class="btn-advanced"
          :class="{ active: showAdvanced }"
          @click="showAdvanced = !showAdvanced">
          ⚙️ 進階篩選
          <span v-if="activeFilterCount > 0" class="filter-badge">{{ activeFilterCount }}</span>
        </button>
        <!-- 清除全部 -->
        <button v-if="hasAnyFilter" class="btn-clear-all" @click="clearAllFilters">
          ✕ 清除篩選
        </button>
      </div>

      <!-- 進階篩選面板（format 1 only） -->
      <div v-if="tablefomrat === 1 && showAdvanced" class="advanced-panel">
        <div class="adv-row">
          <div class="adv-group">
            <label>類別</label>
            <select v-model="filters.item">
              <option value="">全部</option>
              <option v-for="c in categoryList" :key="c" :value="c">{{ c }}</option>
            </select>
          </div>
          <div class="adv-group">
            <label>付款方式</label>
            <select v-model="filters.account">
              <option value="">全部</option>
              <option v-for="a in accountList" :key="a" :value="a">{{ a }}</option>
            </select>
          </div>
          <div class="adv-group">
            <label>金額最小</label>
            <input type="number" v-model.number="filters.amountMin" placeholder="0" min="0" />
          </div>
          <div class="adv-group">
            <label>金額最大</label>
            <input type="number" v-model.number="filters.amountMax" placeholder="不限" min="0" />
          </div>
          <div class="adv-group">
            <label>日期從</label>
            <input type="date" v-model="filters.dateFrom" />
          </div>
          <div class="adv-group">
            <label>日期至</label>
            <input type="date" v-model="filters.dateTo" />
          </div>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="empty-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <!-- Table -->
    <div v-else-if="tableData.length > 0" class="table-card">
      <div class="table-summary">
        <span>
          {{ filteredData.length === tableData.length
              ? `共 ${tableData.length} 筆`
              : `篩選後 ${filteredData.length} 筆 / 共 ${tableData.length} 筆` }}
        </span>
        <span v-if="tablefomrat === 1" class="total-amount">${{ formatNumber(filteredTotal) }}</span>
      </div>
      <div class="table-scroll">
        <table>
          <!-- 流水帳表頭 -->
          <thead v-if="tablefomrat === 1">
            <tr>
              <th class="th-sort" @click="setSort(0)">ID <span class="sort-icon">{{ sortIcon(0) }}</span></th>
              <th class="th-sort" @click="setSort(1)">日期 <span class="sort-icon">{{ sortIcon(1) }}</span></th>
              <th class="th-sort" @click="setSort(2)">類別 <span class="sort-icon">{{ sortIcon(2) }}</span></th>
              <th class="th-sort text-right" @click="setSort(3)">金額 <span class="sort-icon">{{ sortIcon(3) }}</span></th>
              <th class="th-sort" @click="setSort(4)">付款方式 <span class="sort-icon">{{ sortIcon(4) }}</span></th>
              <th class="th-sort" @click="setSort(5)">備註 <span class="sort-icon">{{ sortIcon(5) }}</span></th>
              <th>操作</th>
            </tr>
          </thead>
          <!-- 帳單表頭 -->
          <thead v-if="tablefomrat === 2">
            <tr>
              <th class="th-sort" @click="setSort(0)">ID <span class="sort-icon">{{ sortIcon(0) }}</span></th>
              <th class="th-sort" @click="setSort(1)">日期 <span class="sort-icon">{{ sortIcon(1) }}</span></th>
              <th class="th-sort" @click="setSort(2)">來源 <span class="sort-icon">{{ sortIcon(2) }}</span></th>
              <th class="th-sort" @click="setSort(3)">卡別 <span class="sort-icon">{{ sortIcon(3) }}</span></th>
              <th class="th-sort text-right" @click="setSort(4)">金額 <span class="sort-icon">{{ sortIcon(4) }}</span></th>
            </tr>
          </thead>

          <!-- 月收支表頭 -->
          <thead v-if="tablefomrat === 4">
            <tr>
              <th class="th-sort" @click="setSort(0)">月份 <span class="sort-icon">{{ sortIcon(0) }}</span></th>
              <th class="th-sort text-right" @click="setSort(1)">支出 <span class="sort-icon">{{ sortIcon(1) }}</span></th>
              <th class="th-sort text-right" @click="setSort(2)">收入 <span class="sort-icon">{{ sortIcon(2) }}</span></th>
              <th class="th-sort text-right" @click="setSort(3)">損益 <span class="sort-icon">{{ sortIcon(3) }}</span></th>
            </tr>
          </thead>
          <!-- 轉帳表頭 -->
          <thead v-if="tablefomrat === 5">
            <tr>
              <th class="th-sort" @click="setSort(0)">ID <span class="sort-icon">{{ sortIcon(0) }}</span></th>
              <th class="th-sort" @click="setSort(1)">日期 <span class="sort-icon">{{ sortIcon(1) }}</span></th>
              <th class="th-sort" @click="setSort(2)">來源 <span class="sort-icon">{{ sortIcon(2) }}</span></th>
              <th class="th-sort" @click="setSort(3)">目標 <span class="sort-icon">{{ sortIcon(3) }}</span></th>
              <th class="th-sort text-right" @click="setSort(4)">金額 <span class="sort-icon">{{ sortIcon(4) }}</span></th>
              <th>操作</th>
            </tr>
          </thead>

          <!-- 流水帳內容 -->
          <tbody v-if="tablefomrat === 1">
            <tr v-for="(row, i) in sortedData" :key="i">
              <td class="cell-id">{{ row[0] }}</td>
              <td class="cell-date">{{ row[1] }}</td>
              <td><span class="badge badge-blue">{{ row[2] }}</span></td>
              <td class="text-right cell-amount">${{ formatCurrency(row[3]) }}</td>
              <td><span class="badge badge-green">{{ row[4] }}</span></td>
              <td class="cell-comment">{{ row[5] || '-' }}</td>
              <td class="cell-actions">
                <button class="btn-edit" @click="openEdit(row)">✏️</button>
                <button class="btn-copy" @click="openCopy(row)">📋</button>
                <button class="btn-delete" @click="confirmDelete(row)">🗑️</button>
              </td>
            </tr>
            <tr v-if="sortedData.length === 0">
              <td colspan="7" class="no-result">找不到符合條件的記錄</td>
            </tr>
          </tbody>

          <!-- 月轉帳內容 -->
          <tbody v-else-if="tablefomrat === 5">
            <tr v-for="(row, i) in sortedData" :key="i">
              <td class="cell-id">{{ row[0] }}</td>
              <td class="cell-date">{{ row[1] }}</td>
              <td><span class="badge badge-green">{{ row[2] }}</span></td>
              <td><span class="badge badge-blue">{{ row[3] }}</span></td>
              <td class="text-right cell-amount">${{ formatCurrency(row[4]) }}</td>
              <td class="cell-actions">
                <button class="btn-edit" @click="openTransferEdit(row)">✏️</button>
                <button class="btn-copy" @click="openTransferCopy(row)">📋</button>
                <button class="btn-delete" @click="confirmTransferDelete(row)">🗑️</button>
              </td>
            </tr>
            <tr v-if="sortedData.length === 0">
              <td colspan="6" class="no-result">找不到符合條件的記錄</td>
            </tr>
          </tbody>

          <!-- 其他格式內容 -->
          <tbody v-else>
            <tr v-for="(row, i) in sortedData" :key="i">
              <td v-for="(cell, ci) in row" :key="ci"
                :class="{
                  'text-right': isNumeric(cell),
                  'cell-positive': tablefomrat === 4 && ci === 3 && parseNum(cell) > 0,
                  'cell-negative': tablefomrat === 4 && ci === 3 && parseNum(cell) < 0
                }">
                {{ formatCell(cell, ci) }}
              </td>
            </tr>
            <tr v-if="sortedData.length === 0">
              <td colspan="10" class="no-result">找不到符合條件的記錄</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Empty -->
    <div v-else class="empty-state">
      <div class="empty-icon">📭</div>
      <h3>暫無資料</h3>
      <p>請選擇上方的篩選條件查看記帳記錄</p>
    </div>

    <!-- ── 編輯 Modal ── -->
    <div v-if="editModal.show" class="modal-overlay" @click.self="closeEdit">
      <div class="modal">
        <div class="modal-header">
          <h3>✏️ 編輯記錄 #{{ editModal.id }}</h3>
          <button class="modal-close" @click="closeEdit">✕</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>日期</label>
            <input type="date" v-model="editModal.date" />
          </div>
          <div class="form-group">
            <label>類別</label>
            <select v-model="editModal.item">
              <option v-for="c in categoryList" :key="c" :value="c">{{ c }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>金額</label>
            <input type="number" v-model.number="editModal.price" min="0" />
          </div>
          <div class="form-group">
            <label>付款方式</label>
            <select v-model="editModal.account">
              <option v-for="a in accountList" :key="a" :value="a">{{ a }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>備註</label>
            <input type="text" v-model="editModal.comment" placeholder="選填" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="closeEdit">取消</button>
          <button class="btn-save" :disabled="editModal.saving" @click="saveEdit">
            {{ editModal.saving ? '儲存中...' : '💾 儲存' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── 複製 Modal ── -->
    <div v-if="copyModal.show" class="modal-overlay" @click.self="closeCopy">
      <div class="modal">
        <div class="modal-header">
          <h3>📋 複製為今日記錄</h3>
          <button class="modal-close" @click="closeCopy">✕</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>類別</label>
            <select v-model="copyModal.item">
              <option v-for="c in categoryList" :key="c" :value="c">{{ c }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>金額</label>
            <input type="number" v-model.number="copyModal.price" min="0" />
          </div>
          <div class="form-group">
            <label>付款方式</label>
            <select v-model="copyModal.account">
              <option v-for="a in accountList" :key="a" :value="a">{{ a }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>備註</label>
            <input type="text" v-model="copyModal.comment" placeholder="選填" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="closeCopy">取消</button>
          <button class="btn-save" :disabled="copyModal.saving" @click="saveCopy">
            {{ copyModal.saving ? '新增中...' : '📋 新增今日記錄' }}
          </button>
        </div>
      </div>
    </div>

        <!-- ── 刪除確認 Modal ── -->
    <div v-if="deleteModal.show" class="modal-overlay" @click.self="closeDelete">
      <div class="modal modal-sm">
        <div class="modal-header">
          <h3>🗑️ 確認刪除</h3>
          <button class="modal-close" @click="closeDelete">✕</button>
        </div>
        <div class="modal-body">
          <p>確定要刪除以下記錄嗎？</p>
          <div class="delete-preview">
            <span class="badge badge-blue">{{ deleteModal.item }}</span>
            <strong>${{ formatCurrency(deleteModal.price) }}</strong>
            <span class="text-light">{{ deleteModal.date }}</span>
          </div>
          <p class="delete-warn">⚠️ 此操作無法復原</p>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="closeDelete">取消</button>
          <button class="btn-danger" :disabled="deleteModal.deleting" @click="doDelete">
            {{ deleteModal.deleting ? '刪除中...' : '🗑️ 確認刪除' }}
          </button>
        </div>
      </div>
    </div>


    <!-- ── 轉帳編輯 Modal ── -->
    <div v-if="transferEditModal.show" class="modal-overlay" @click.self="closeTransferEdit">
      <div class="modal">
        <div class="modal-header">
          <h3>✏️ 編輯轉帳 #{{ transferEditModal.id }}</h3>
          <button class="modal-close" @click="closeTransferEdit">✕</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>日期</label>
            <input type="date" v-model="transferEditModal.date" />
          </div>
          <div class="form-group">
            <label>來源</label>
            <select v-model="transferEditModal.source">
              <option v-for="a in accountList" :key="a" :value="a">{{ a }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>目標</label>
            <select v-model="transferEditModal.target">
              <option v-for="a in accountList" :key="a" :value="a">{{ a }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>金額</label>
            <input type="number" v-model.number="transferEditModal.amount" min="0" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="closeTransferEdit">取消</button>
          <button class="btn-save" :disabled="transferEditModal.saving" @click="saveTransferEdit">
            {{ transferEditModal.saving ? '儲存中...' : '💾 儲存' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── 轉帳複製 Modal ── -->
    <div v-if="transferCopyModal.show" class="modal-overlay" @click.self="closeTransferCopy">
      <div class="modal">
        <div class="modal-header">
          <h3>📋 複製為今日轉帳</h3>
          <button class="modal-close" @click="closeTransferCopy">✕</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>來源</label>
            <select v-model="transferCopyModal.source">
              <option v-for="a in accountList" :key="a" :value="a">{{ a }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>目標</label>
            <select v-model="transferCopyModal.target">
              <option v-for="a in accountList" :key="a" :value="a">{{ a }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>金額</label>
            <input type="number" v-model.number="transferCopyModal.amount" min="0" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="closeTransferCopy">取消</button>
          <button class="btn-save" :disabled="transferCopyModal.saving" @click="saveTransferCopy">
            {{ transferCopyModal.saving ? '新增中...' : '📋 新增今日轉帳' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── 轉帳刪除確認 Modal ── -->
    <div v-if="transferDeleteModal.show" class="modal-overlay" @click.self="closeTransferDelete">
      <div class="modal modal-sm">
        <div class="modal-header">
          <h3>🗑️ 確認刪除轉帳</h3>
          <button class="modal-close" @click="closeTransferDelete">✕</button>
        </div>
        <div class="modal-body">
          <p>確定要刪除以下轉帳記錄嗎？</p>
          <div class="delete-preview">
            <span class="badge badge-green">{{ transferDeleteModal.source }}</span>
            <span>→</span>
            <span class="badge badge-blue">{{ transferDeleteModal.target }}</span>
            <strong>${{ formatCurrency(transferDeleteModal.amount) }}</strong>
          </div>
          <p class="delete-warn">⚠️ 此操作無法復原</p>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="closeTransferDelete">取消</button>
          <button class="btn-danger" :disabled="transferDeleteModal.deleting" @click="doTransferDelete">
            {{ transferDeleteModal.deleting ? '刪除中...' : '🗑️ 確認刪除' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Toast -->
    <div v-if="toast.show" :class="['toast', `toast-${toast.type}`]">
      {{ toast.message }}
    </div>
  </div>
</template>

<script>
import axios from 'axios'
axios.defaults.baseURL = 'https://mb.kman.idv.tw:9579/';

export default {
  data() {
    return {
      tableData: [],
      tablefomrat: 1,
      activeFilter: null,
      loading: false,
      categoryList: [],
      accountList: [],
      currentFilter: { month: null, format: null, key: null, ep: null },

      // 搜尋 & 進階篩選
      search: '',
      showAdvanced: false,
      filters: { item: '', account: '', amountMin: null, amountMax: null, dateFrom: '', dateTo: '' },

      editModal: {
        show: false, saving: false,
        id: null, date: '', item: '', price: 0, account: '', comment: ''
      },
      copyModal: {
        show: false, saving: false,
        item: '', price: 0, account: '', comment: ''
      },
      deleteModal: {
        show: false, deleting: false,
        id: null, date: '', item: '', price: 0
      },
      transferEditModal: {
        show: false, saving: false,
        id: null, date: '', source: '', target: '', amount: 0
      },
      transferCopyModal: {
        show: false, saving: false,
        source: '', target: '', amount: 0
      },
      transferDeleteModal: {
        show: false, deleting: false,
        id: null, source: '', target: '', amount: 0
      },
      toast: { show: false, message: '', type: 'success' },
      sort: { col: null, dir: 'asc' },

      filterButtons: [
        { key: 'today',       icon: '📅', label: '本日',    action: () => this.fetchMonth(0, 1, 'today') },
        { key: 'thismonth',   icon: '📆', label: '本月',    action: () => this.fetchMonth(1, 1, 'thismonth') },
        { key: 'lastmonth',   icon: '◀️', label: '上個月',  action: () => this.fetchMonth(2, 1, 'lastmonth') },
        { key: 'last2months', icon: '⏪', label: '前兩個月', action: () => this.fetchMonth(3, 1, 'last2months') },
        { key: 'transfer',    icon: '🔄', label: '月轉帳',  action: () => this.fetchMonth(5, 5, 'transfer') },
        { key: 'bill',        icon: '💳', label: '帳單',    action: () => this.fetchEndpoint('getbill', 2, 'bill') },

        { key: 'summary',     icon: '📊', label: '月收支',  action: () => this.fetchMonth(4, 4, 'summary') }
      ]
    };
  },
  computed: {
    // 進階篩選是否有作用中的條件
    activeFilterCount() {
      let n = 0;
      if (this.filters.item)                                              n++;
      if (this.filters.account)                                           n++;
      if (this.filters.amountMin !== null && this.filters.amountMin !== '') n++;
      if (this.filters.amountMax !== null && this.filters.amountMax !== '') n++;
      if (this.filters.dateFrom)                                          n++;
      if (this.filters.dateTo)                                            n++;
      return n;
    },
    hasAnyFilter() {
      return this.search.trim() !== '' || this.activeFilterCount > 0;
    },

    // 搜尋 + 進階篩選後的資料
    filteredData() {
      let rows = this.tableData;
      const q = this.search.trim().toLowerCase();

      // 模糊搜尋：跨所有欄位
      if (q) {
        rows = rows.filter(row =>
          row.some(cell => cell !== null && cell !== undefined &&
            String(cell).toLowerCase().includes(q))
        );
      }

      // 資產視圖：隱藏現值為 0 的項目
      if (this.tablefomrat === 3) {
        rows = rows.filter(r => this.parseNum(r[1]) !== 0);
      }

      // 進階篩選（只對流水帳 format 1）
      if (this.tablefomrat === 1) {
        const { item, account, amountMin, amountMax, dateFrom, dateTo } = this.filters;
        if (item)    rows = rows.filter(r => String(r[2]) === item);
        if (account) rows = rows.filter(r => String(r[4]) === account);
        if (amountMin !== null && amountMin !== '')
          rows = rows.filter(r => this.parseNum(r[3]) >= amountMin);
        if (amountMax !== null && amountMax !== '')
          rows = rows.filter(r => this.parseNum(r[3]) <= amountMax);
        if (dateFrom)
          rows = rows.filter(r => String(r[1]).substring(0, 10) >= dateFrom);
        if (dateTo)
          rows = rows.filter(r => String(r[1]).substring(0, 10) <= dateTo);
      }

      return rows;
    },

    // 排序
    sortedData() {
      const { col, dir } = this.sort;
      if (col === null) return this.filteredData;
      const isDateStr = v => typeof v === 'string' && /^\d{4}[-\/]\d{2}/.test(v);
      return [...this.filteredData].sort((a, b) => {
        const av = a[col], bv = b[col];
        let cmp;
        if (isDateStr(av) && isDateStr(bv)) {
          cmp = av.localeCompare(bv);
        } else {
          const an = this.parseNum(av), bn = this.parseNum(bv);
          if (!isNaN(an) && !isNaN(bn)) {
            cmp = an - bn;
          } else {
            cmp = String(av ?? '').localeCompare(String(bv ?? ''), 'zh-TW');
          }
        }
        return dir === 'asc' ? cmp : -cmp;
      });
    },

    // 篩選後金額總計
    filteredTotal() {
      if (this.tablefomrat !== 1) return 0;
      return this.filteredData.reduce((sum, row) => sum + (this.parseNum(row[3]) || 0), 0);
    }
  },
  mounted() {
    this.loadSelectOptions();
  },
  methods: {
    // ── 下拉選單 ──
    async loadSelectOptions() {
      const headers = { Authorization: `Bearer ${this.$store.getters.setToken}` };
      try {
        const [catRes, accRes] = await Promise.all([
          axios.get('get_pay_item', { headers }),
          axios.get('get_transfer_item', { headers })
        ]);
        this.categoryList = catRes.data;
        this.accountList  = accRes.data;
      } catch (e) { console.error('loadSelectOptions fail', e); }
    },

    // ── 格式化 ──
    formatCurrency(val) {
      const num = this.parseNum(val);
      if (isNaN(num)) return '-';
      return num.toLocaleString();
    },
    formatNumber(val) {
      const num = this.parseNum(val);
      if (isNaN(num)) return '0';
      return num.toLocaleString();
    },
    formatCell(val, colIndex) {
      if (colIndex === 1) return val;
      if (typeof val === 'string' && /^\d{4}[-\/]\d{2}/.test(val)) return val;
      const num = this.parseNum(val);
      if (!isNaN(num)) return num.toLocaleString();
      return val;
    },
    parseNum(v) {
      if (v === null || v === undefined || v === '') return NaN;
      if (typeof v === 'number') return v;
      const str = String(v).replace(/[$,]/g, '').trim();
      const num = parseFloat(str);
      return isNaN(num) ? NaN : num;
    },
    isNumeric(v) {
      if (v === null || v === undefined) return false;
      if (typeof v === 'number') return true;
      const str = String(v).replace(/[$,]/g, '').trim();
      return !isNaN(parseFloat(str)) && isFinite(str);
    },

    // ── 資料載入 ──
    async fetchMonth(month, format, key) {
      this.loading = true;
      this.activeFilter = key;
      this.tableData = [];
      this.tablefomrat = format;
      this.sort = { col: null, dir: 'asc' };
      this.resetSearchFilters();
      this.currentFilter = { month, format, key, ep: null };
      try {
        const r = await axios.get(`getmonth/${month}`, {
          headers: { Authorization: `Bearer ${this.$store.getters.setToken}` }
        });
        const rows = r.data.transaction || [];
        this.tableData = format === 1
          ? rows.filter(row => String(row[2]).toLowerCase() !== 'total')
          : rows;
      } catch (e) { console.error(e); }
      finally { this.loading = false; }
    },

    async fetchEndpoint(ep, format, key) {
      this.loading = true;
      this.activeFilter = key;
      this.tableData = [];
      this.tablefomrat = format;
      this.sort = { col: null, dir: 'asc' };
      this.resetSearchFilters();
      this.currentFilter = { month: null, format, key, ep };
      try {
        const r = await axios.get(ep, {
          headers: { Authorization: `Bearer ${this.$store.getters.setToken}` }
        });
        this.tableData = r.data.transaction;
      } catch (e) { console.error(e); }
      finally { this.loading = false; }
    },

    async reloadCurrent() {
      const f = this.currentFilter;
      if (f.ep) await this.fetchEndpoint(f.ep, f.format, f.key);
      else if (f.month !== null) await this.fetchMonth(f.month, f.format, f.key);
    },

    // ── 搜尋 & 篩選 ──
    onSearchInput() {
      // 留給未來 debounce 用
    },
    resetSearchFilters() {
      this.search = '';
      this.showAdvanced = false;
      this.filters = { item: '', account: '', amountMin: null, amountMax: null, dateFrom: '', dateTo: '' };
    },
    clearAllFilters() {
      this.search = '';
      this.filters = { item: '', account: '', amountMin: null, amountMax: null, dateFrom: '', dateTo: '' };
    },

    // ── 排序 ──
    setSort(col) {
      if (this.sort.col === col) {
        this.sort.dir = this.sort.dir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sort.col = col;
        this.sort.dir = 'asc';
      }
    },
    sortIcon(col) {
      if (this.sort.col !== col) return '↕';
      return this.sort.dir === 'asc' ? '↑' : '↓';
    },

    // ── 編輯 ──
    openEdit(row) {
      this.editModal = {
        show: true, saving: false,
        id:      row[0],
        date:    row[1]?.substring(0, 10) || '',
        item:    row[2] || '',
        price:   this.parseNum(row[3]) || 0,
        account: row[4] || '',
        comment: row[5] || ''
      };
    },
    closeEdit() { this.editModal.show = false; },
    async saveEdit() {
      this.editModal.saving = true;
      try {
        await axios.post('updateevent', {
          id:      this.editModal.id,
          date:    this.editModal.date,
          item:    this.editModal.item,
          price:   this.editModal.price,
          account: this.editModal.account,
          comment: this.editModal.comment,
          token:   this.$store.getters.setToken
        });
        this.closeEdit();
        await this.reloadCurrent();
        this.showToast('✅ 已更新', 'success');
      } catch (e) {
        console.error(e);
        this.showToast('❌ 更新失敗', 'error');
      } finally { this.editModal.saving = false; }
    },

    // ── 複製 ──
    openCopy(row) {
      this.copyModal = {
        show: true, saving: false,
        item:    row[2] || '',
        price:   this.parseNum(row[3]) || 0,
        account: row[4] || '',
        comment: row[5] || ''
      };
    },
    closeCopy() { this.copyModal.show = false; },
    async saveCopy() {
      this.copyModal.saving = true;
      try {
        await axios.post('addevent', {
          curritem1: this.copyModal.item,
          curritem2: this.copyModal.account,
          cost:      this.copyModal.price,
          btext:     this.copyModal.comment,
          token:     this.$store.getters.setToken,
          yesterday: false
        });
        this.closeCopy();
        await this.reloadCurrent();
        this.showToast('📋 已複製為今日記錄', 'success');
      } catch (e) {
        console.error(e);
        this.showToast('❌ 複製失敗', 'error');
      } finally { this.copyModal.saving = false; }
    },

    // ── 刪除 ──
    confirmDelete(row) {
      this.deleteModal = {
        show: true, deleting: false,
        id: row[0], date: row[1], item: row[2], price: row[3]
      };
    },
    closeDelete() { this.deleteModal.show = false; },
    async doDelete() {
      this.deleteModal.deleting = true;
      try {
        await axios.post('deleteevent', {
          id:    this.deleteModal.id,
          token: this.$store.getters.setToken
        });
        this.closeDelete();
        await this.reloadCurrent();
        this.showToast('🗑️ 已刪除', 'success');
      } catch (e) {
        console.error(e);
        this.showToast('❌ 刪除失敗', 'error');
      } finally { this.deleteModal.deleting = false; }
    },

    // ── 轉帳編輯 ──
    openTransferEdit(row) {
      this.transferEditModal = {
        show: true, saving: false,
        id:     row[0],
        date:   row[1]?.substring(0, 10) || '',
        source: row[2] || '',
        target: row[3] || '',
        amount: this.parseNum(row[4]) || 0
      };
    },
    closeTransferEdit() { this.transferEditModal.show = false; },
    async saveTransferEdit() {
      this.transferEditModal.saving = true;
      try {
        await axios.post('updatetransfer', {
          id:     this.transferEditModal.id,
          date:   this.transferEditModal.date,
          source: this.transferEditModal.source,
          target: this.transferEditModal.target,
          amount: this.transferEditModal.amount,
          token:  this.$store.getters.setToken
        });
        this.closeTransferEdit();
        await this.reloadCurrent();
        this.showToast('✅ 已更新', 'success');
      } catch (e) {
        console.error(e);
        this.showToast('❌ 更新失敗', 'error');
      } finally { this.transferEditModal.saving = false; }
    },

    // ── 轉帳複製 ──
    openTransferCopy(row) {
      this.transferCopyModal = {
        show: true, saving: false,
        source: row[2] || '',
        target: row[3] || '',
        amount: this.parseNum(row[4]) || 0
      };
    },
    closeTransferCopy() { this.transferCopyModal.show = false; },
    async saveTransferCopy() {
      this.transferCopyModal.saving = true;
      try {
        await axios.post('transferevent', {
          curritem1: this.transferCopyModal.source,
          curritem2: this.transferCopyModal.target,
          cost:      this.transferCopyModal.amount,
          token:     this.$store.getters.setToken,
          yesterday: false
        });
        this.closeTransferCopy();
        await this.reloadCurrent();
        this.showToast('📋 已複製為今日轉帳', 'success');
      } catch (e) {
        console.error(e);
        this.showToast('❌ 複製失敗', 'error');
      } finally { this.transferCopyModal.saving = false; }
    },

    // ── 轉帳刪除 ──
    confirmTransferDelete(row) {
      this.transferDeleteModal = {
        show: true, deleting: false,
        id:     row[0],
        source: row[2],
        target: row[3],
        amount: row[4]
      };
    },
    closeTransferDelete() { this.transferDeleteModal.show = false; },
    async doTransferDelete() {
      this.transferDeleteModal.deleting = true;
      try {
        await axios.post('deletetransfer', {
          id:    this.transferDeleteModal.id,
          token: this.$store.getters.setToken
        });
        this.closeTransferDelete();
        await this.reloadCurrent();
        this.showToast('🗑️ 已刪除', 'success');
      } catch (e) {
        console.error(e);
        this.showToast('❌ 刪除失敗', 'error');
      } finally { this.transferDeleteModal.deleting = false; }
    },

    // ── Toast ──
    showToast(message, type = 'success') {
      this.toast = { show: true, message, type };
      setTimeout(() => { this.toast.show = false; }, 2500);
    },

    // Legacy aliases
    today()                    { this.fetchMonth(0, 1, 'today'); },
    thismonth()                { this.fetchMonth(1, 1, 'thismonth'); },
    lastmonth()                { this.fetchMonth(2, 1, 'lastmonth'); },
    last2months()              { this.fetchMonth(3, 1, 'last2months'); },
    monthtransfer()            { this.fetchMonth(5, 5, 'transfer'); },
    monthlyfinancialsummary()  { this.fetchMonth(4, 4, 'summary'); },
    getbill()                  { this.fetchEndpoint('getbill', 2, 'bill'); },
    getcurrent()               { this.fetchEndpoint('getcurrent', 3, 'current'); }
  }
};
</script>

<style scoped>
.page-header { margin-bottom: 20px; }
.page-header h1 { font-size: 24px; font-weight: 700; }
.page-header p  { font-size: 14px; color: var(--text-light); margin-top: 2px; }

/* ── Filter bar ── */
.filter-bar {
  display: flex; flex-wrap: wrap; gap: 6px;
  background: var(--card); border-radius: var(--radius);
  box-shadow: var(--shadow); padding: 10px 12px; margin-bottom: 10px;
}
.filter-btn {
  padding: 8px 14px; font-size: 13px; font-weight: 600;
  background: #f9fafb; border: 1px solid var(--border); border-radius: 8px;
  cursor: pointer; color: var(--text); transition: all 0.15s;
}
.filter-btn:hover  { border-color: var(--primary); color: var(--primary); background: #eef2ff; }
.filter-btn.active { background: var(--primary); color: #fff; border-color: var(--primary); }

/* ── Search panel ── */
.search-panel {
  background: var(--card); border-radius: var(--radius);
  box-shadow: var(--shadow); padding: 12px 14px; margin-bottom: 10px;
}
.search-row {
  display: flex; gap: 8px; align-items: center; flex-wrap: wrap;
}
.search-input-wrap {
  position: relative; flex: 1; min-width: 180px;
}
.search-icon {
  position: absolute; left: 10px; top: 50%; transform: translateY(-50%);
  font-size: 14px; pointer-events: none;
}
.search-input {
  width: 100%; padding: 8px 32px 8px 32px; font-size: 14px;
  border: 1px solid var(--border); border-radius: 8px;
  background: #f9fafb; color: var(--text); box-sizing: border-box;
  transition: border-color 0.15s, background 0.15s;
}
.search-input:focus {
  outline: none; border-color: var(--primary); background: #fff;
}
.search-clear {
  position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
  background: none; border: none; cursor: pointer; color: #9ca3af;
  font-size: 13px; padding: 2px 4px; line-height: 1;
}
.search-clear:hover { color: #374151; }

.btn-advanced {
  padding: 8px 14px; font-size: 13px; font-weight: 600;
  background: #f9fafb; border: 1px solid var(--border); border-radius: 8px;
  cursor: pointer; color: var(--text); white-space: nowrap;
  display: flex; align-items: center; gap: 4px; transition: all 0.15s;
}
.btn-advanced:hover,
.btn-advanced.active { border-color: var(--primary); color: var(--primary); background: #eef2ff; }
.filter-badge {
  display: inline-flex; align-items: center; justify-content: center;
  width: 18px; height: 18px; border-radius: 50%; font-size: 11px;
  background: var(--primary); color: #fff; font-weight: 700;
}
.btn-clear-all {
  padding: 8px 14px; font-size: 13px; font-weight: 600;
  background: #fff1f2; border: 1px solid #fecdd3; border-radius: 8px;
  cursor: pointer; color: #dc2626; white-space: nowrap; transition: all 0.15s;
}
.btn-clear-all:hover { background: #fecdd3; }

/* ── Advanced panel ── */
.advanced-panel {
  margin-top: 10px; padding-top: 10px; border-top: 1px solid var(--border);
}
.adv-row {
  display: flex; flex-wrap: wrap; gap: 10px;
}
.adv-group {
  display: flex; flex-direction: column; gap: 4px; min-width: 130px; flex: 1;
}
.adv-group label {
  font-size: 11px; font-weight: 600; color: var(--text-light);
  text-transform: uppercase; letter-spacing: 0.4px;
}
.adv-group input,
.adv-group select {
  padding: 7px 9px; font-size: 13px;
  border: 1px solid var(--border); border-radius: 8px;
  background: #f9fafb; color: var(--text);
  transition: border-color 0.15s;
}
.adv-group input:focus,
.adv-group select:focus { outline: none; border-color: var(--primary); background: #fff; }

/* ── Table card ── */
.table-card {
  background: var(--card); border-radius: var(--radius);
  box-shadow: var(--shadow); overflow: hidden;
}
.table-summary {
  display: flex; justify-content: space-between; align-items: center;
  padding: 10px 16px; background: #f9fafb; border-bottom: 1px solid var(--border);
  font-size: 13px; color: var(--text-light);
}
.total-amount { font-weight: 700; color: #dc2626; }
.table-scroll { overflow-x: auto; }

table { width: 100%; border-collapse: collapse; }
thead { background: #f9fafb; }
th {
  padding: 10px 14px; font-size: 12px; font-weight: 600;
  color: var(--text-light); text-transform: uppercase; letter-spacing: 0.5px;
  text-align: left; border-bottom: 1px solid var(--border);
}
td { padding: 10px 14px; font-size: 14px; border-bottom: 1px solid #f3f4f6; }
tr:hover { background: #fafaff; }
.text-right { text-align: right; }

.th-sort { cursor: pointer; user-select: none; transition: color 0.15s, background 0.15s; }
.th-sort:hover { color: var(--primary); background: #eef2ff; }
.sort-icon { font-size: 11px; opacity: 0.5; margin-left: 2px; }
.th-sort:hover .sort-icon { opacity: 1; color: var(--primary); }

.cell-id      { color: #9ca3af; font-size: 12px; }
.cell-date    { white-space: nowrap; }
.cell-amount  { font-weight: 600; color: #dc2626; }
.cell-comment { color: var(--text-light); max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cell-positive { color: #059669; font-weight: 600; }
.cell-negative { color: #dc2626; font-weight: 600; }
.cell-actions  { white-space: nowrap; }
.no-result { text-align: center; padding: 24px; color: #9ca3af; font-size: 14px; }

.badge { display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 12px; font-weight: 600; }
.badge-blue  { background: #eff6ff; color: #1d4ed8; }
.badge-green { background: #ecfdf5; color: #047857; }

.btn-edit, .btn-delete {
  padding: 4px 8px; font-size: 14px; border: none;
  border-radius: 6px; cursor: pointer; transition: background 0.15s;
}
.btn-edit         { background: #eff6ff; }
.btn-edit:hover   { background: #dbeafe; }
.btn-delete       { background: #fff1f2; margin-left: 4px; }
.btn-delete:hover { background: #fecdd3; }
.btn-copy         { background: #f0fdf4; margin-left: 4px; }
.btn-copy:hover   { background: #dcfce7; }

/* ── Modal ── */
.modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.45);
  display: flex; align-items: center; justify-content: center; z-index: 1000;
}
.modal {
  background: #fff; border-radius: 12px; width: 90%; max-width: 420px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2); overflow: hidden;
}
.modal-sm { max-width: 340px; }
.modal-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 16px 20px; border-bottom: 1px solid var(--border);
  font-size: 16px; font-weight: 700;
}
.modal-close { background: none; border: none; font-size: 18px; cursor: pointer; color: #9ca3af; }
.modal-close:hover { color: #374151; }
.modal-body { padding: 20px; }
.modal-footer {
  display: flex; justify-content: flex-end; gap: 8px;
  padding: 16px 20px; border-top: 1px solid var(--border); background: #f9fafb;
}

.form-group { margin-bottom: 14px; }
.form-group label {
  display: block; font-size: 12px; font-weight: 600;
  color: var(--text-light); margin-bottom: 4px;
  text-transform: uppercase; letter-spacing: 0.4px;
}
.form-group input,
.form-group select {
  width: 100%; padding: 8px 10px; font-size: 14px;
  border: 1px solid var(--border); border-radius: 8px;
  background: #fff; color: var(--text); box-sizing: border-box;
  transition: border-color 0.15s;
}
.form-group input:focus,
.form-group select:focus { outline: none; border-color: var(--primary); }

.btn-cancel {
  padding: 8px 16px; font-size: 14px; font-weight: 600;
  background: #f3f4f6; border: 1px solid var(--border); border-radius: 8px;
  cursor: pointer; color: var(--text);
}
.btn-cancel:hover { background: #e5e7eb; }
.btn-save {
  padding: 8px 18px; font-size: 14px; font-weight: 600;
  background: var(--primary); border: none; border-radius: 8px;
  cursor: pointer; color: #fff; transition: opacity 0.15s;
}
.btn-save:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-save:not(:disabled):hover { opacity: 0.88; }
.btn-danger {
  padding: 8px 18px; font-size: 14px; font-weight: 600;
  background: #dc2626; border: none; border-radius: 8px;
  cursor: pointer; color: #fff; transition: opacity 0.15s;
}
.btn-danger:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-danger:not(:disabled):hover { opacity: 0.88; }

.delete-preview {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 14px; background: #fff1f2; border-radius: 8px;
  margin: 10px 0; flex-wrap: wrap;
}
.delete-preview strong { color: #dc2626; }
.text-light { color: #9ca3af; font-size: 13px; }
.delete-warn { font-size: 13px; color: #9ca3af; margin-top: 6px; }

/* ── Toast ── */
.toast {
  position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
  padding: 10px 20px; border-radius: 8px; font-size: 14px; font-weight: 600;
  z-index: 2000; box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  animation: fadeInUp 0.2s ease;
}
.toast-success { background: #059669; color: #fff; }
.toast-error   { background: #dc2626; color: #fff; }
@keyframes fadeInUp {
  from { opacity: 0; transform: translateX(-50%) translateY(10px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
}

/* ── Empty ── */
.empty-state {
  background: var(--card); border-radius: var(--radius);
  box-shadow: var(--shadow); padding: 48px 20px; text-align: center;
}
.empty-icon { font-size: 48px; margin-bottom: 12px; }
.empty-state h3 { font-size: 18px; font-weight: 600; margin-bottom: 4px; }
.empty-state p  { font-size: 14px; color: var(--text-light); }

.spinner {
  width: 32px; height: 32px; border: 3px solid var(--border);
  border-top-color: var(--primary); border-radius: 50%;
  animation: spin 0.8s linear infinite; margin: 0 auto 12px;
}
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 640px) {
  .filter-btn { padding: 6px 10px; font-size: 12px; }
  th, td { padding: 8px 10px; }
  .adv-group { min-width: 100%; }
}
</style>
